import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useMutation } from "react-query";

import { theme } from "../../../theme";
import { ReactComponent as UploaderBtn } from "../../../assets/icons/UploaderBtn.svg";
import ImagePreviewBox from "../../atoms/ImagePreviewBox";

import useModals from "../../../hooks/useModals";

import LoadingBox from "../../atoms/LoadingBox";

import { SERVICE_KEY } from "../../../shared/constants";
import ExplainWrapper from "../ExplainWrapper";
import { ValidationType } from "../../atoms/Explain/types";
import Explain from "../../atoms/Explain";

import { uploadImage } from "./apis";
import { Media } from "../../../model/Media";
import ImagePreviewModal from "../../../pages/UserInfo/components/ImagePreviewModal";
import { HttpError } from "../../../network/types";

interface Props {
  imageList?: Media[];
  max?: number;
  fileMaxMbLength?: number;
  folder: string;
  multiple?: boolean;
  inputKey?: string;
  label?: string;
  onUpload: (e: Media[]) => void;
  onRemove?: (e: Media[]) => void;
  imageExplain?: string[];
  errorExplain?: string;
  disabled?: boolean;
}
const PreviewUploader: React.FC<Props> = ({
  imageList,
  max,
  folder,
  inputKey,
  fileMaxMbLength = 1024 * 1024 * 3,
  multiple,
  label,
  onUpload,
  onRemove,
  imageExplain,
  errorExplain,
  disabled,
}) => {
  const ImageUploadMutation = useMutation(uploadImage, {
    onSuccess: (res) => {
      const newResult = res;
      onUpload([...mediaList, ...newResult]);
      setMediaList([...mediaList, ...newResult]);
      setPreImageNum(0);
    },
    onError: (error: HttpError) => {
      setPreImageNum(0);
      setStatus({ explain: error.message, status: "error" });
    },
  });
  const inputRef = useRef<any>(null);
  const { openModal } = useModals();
  const [preImageNum, setPreImageNum] = useState<number>(0);
  const [status, setStatus] = useState<ValidationType>({
    status: errorExplain ? "error" : undefined,
    explain: errorExplain,
  });

  const [mediaList, setMediaList] = useState<Media[]>(imageList || []);

  const onChange = (e: any) => {
    const { files } = e.target;
    const newData = new FormData();
    newData.append("service", SERVICE_KEY);
    newData.append("folder", folder);
    let newPreImageNum = preImageNum;
    const fileEntries = Object.values<File>(files);
    setStatus({ status: undefined, explain: undefined });
    let isError = false;
    for (let i = 0; i < fileEntries.length; i += 1) {
      if (fileEntries[i].size < fileMaxMbLength)
        if (max) {
          if (mediaList.length + (newPreImageNum + 1) <= max) {
            newData.append("image", fileEntries[i]);
            newPreImageNum += 1;
          }
        } else {
          newData.append("image", fileEntries[i]);
          newPreImageNum += 1;
        }
      else {
        isError = true;
        setStatus({
          status: "error",
          explain: `파일 사이즈가 제한을 넘어섭니다.`,
        });
      }
    }
    if (inputRef.current) inputRef.current.value = "";
    setPreImageNum(newPreImageNum);
    if (isError !== true) ImageUploadMutation.mutate(newData);
  };
  const renderLoadingBox = () => {
    const boxes = [];
    for (let i = 0; i < preImageNum; i += 1) {
      boxes.push(<LoadingBox key={`preImage${i}`} />);
    }
    return boxes;
  };

  const onDetailBox = (e: string) => {
    openModal(
      <ImagePreviewModal
        folder={mediaList.length > 0 ? mediaList[0].folder : folder}
        image={e}
        medias={mediaList}
      />
    );
  };
  const onRemoveBox = (index: number) => {
    const newMediaList = [...mediaList];
    newMediaList.splice(index, 1);
    setMediaList(newMediaList);

    if (onRemove) onRemove(newMediaList);
  };

  useEffect(() => {
    setStatus({
      status: errorExplain ? "error" : undefined,
      explain: errorExplain,
    });
  }, [errorExplain]);
  useEffect(() => {
    if (imageList) {
      setMediaList(imageList);
    }
  }, [imageList, preImageNum]);
  return (
    <>
      <ExplainWrapper status={status}>
        <UploaderWrapper>
          {mediaList?.map((value, index) => {
            if (value) {
              const {
                id,
                folder = "",
                fileName = "",
                bufferFullUrl = "",
              } = value;
              return (
                <ImagePreviewBox
                  disabled={disabled}
                  label={label}
                  onRemove={() => onRemoveBox(index)}
                  onDetail={onDetailBox}
                  key={id || 0 + index + fileName}
                  folder={folder}
                  imgName={bufferFullUrl}
                />
              );
            }
            return null;
          })}
          {renderLoadingBox()}
          {renderLoadingBox().length === 0 ? (
            max ? (
              mediaList.length < max && (
                <StyledLabel htmlFor={`preview_uploader${inputKey}`}>
                  <StyledButton>
                    <UploaderBtn />
                    <StyledTextWrapper>이미지 업로드</StyledTextWrapper>
                  </StyledButton>
                </StyledLabel>
              )
            ) : (
              <StyledLabel htmlFor={`preview_uploader${inputKey}`}>
                <StyledButton>
                  <UploaderBtn />
                  <StyledTextWrapper>이미지 업로드</StyledTextWrapper>
                </StyledButton>
              </StyledLabel>
            )
          ) : null}
          <StyledUploader
            id={`preview_uploader${inputKey}`}
            name="preview_uploader"
            type="file"
            ref={inputRef}
            accept="image/jpeg,image/png,image/gif"
            multiple={multiple}
            onChange={onChange}
          />
        </UploaderWrapper>
      </ExplainWrapper>
    </>
  );
};
export default PreviewUploader;
const StyledUploader = styled.input`
  visibility: hidden;
  width: 0px;
  background-color: black;
  display: flex;
  justify-content: flex-start;
  overflow-y: hidden;
`;
const StyledLabel = styled.label``;

const StyledButton = styled.div`
  width: 100px;
  height: 100px;
  display: flex;
  cursor: pointer;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0px 0px 0px 0px;
  border: 1px dashed ${theme.colors.gray7};
`;
const StyledTextWrapper = styled.div`
  ${theme.typo.body.r14};
  margin-top: 4px;
`;
const UploaderWrapper = styled.div`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  justify-content: flex-start;
  flex-wrap: wrap;
  max-height: 450px;
  width: 100%;
`;
